import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Apple, Fish, Beef, AlertCircle, Plus, Minus } from 'lucide-react';
import { format, startOfWeek, endOfWeek, isWithinInterval, parseISO } from 'date-fns';
import { DatabaseSchema, FoodLogEntry } from '../../types';

interface Props {
  database: DatabaseSchema | null;
  onAddFoodLog: (entry: FoodLogEntry) => Promise<void>;
}

export const DailyFoodLog: React.FC<Props> = ({ database, onAddFoodLog }) => {
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const existing = (database?.foodLog ?? []).find((f) => f.date === todayStr);

  const [fruitVeg, setFruitVeg] = useState<number>(existing?.fruitVegServings ?? 0);
  const [oilyFish, setOilyFish] = useState<boolean>(existing?.oilyFish ?? false);
  const [redMeat, setRedMeat] = useState<boolean>(existing?.redMeat ?? false);
  const [highPurine, setHighPurine] = useState<boolean>(existing?.highPurine ?? false);
  const [addedSugar, setAddedSugar] = useState<FoodLogEntry['addedSugar']>(existing?.addedSugar ?? 'none');
  const [notes, setNotes] = useState(existing?.notes ?? '');
  const [saving, setSaving] = useState(false);

  const weekCounts = useMemo(() => {
    const now = new Date();
    const start = startOfWeek(now, { weekStartsOn: 1 });
    const end = endOfWeek(now, { weekStartsOn: 1 });
    const thisWeek = (database?.foodLog ?? []).filter((f) => {
      try {
        return isWithinInterval(parseISO(f.date), { start, end });
      } catch {
        return false;
      }
    });
    return {
      oilyFish: thisWeek.filter((f) => f.oilyFish).length,
      redMeat: thisWeek.filter((f) => f.redMeat).length,
      highPurine: thisWeek.filter((f) => f.highPurine).length,
      avgFruitVeg: thisWeek.length
        ? thisWeek.reduce((s, f) => s + f.fruitVegServings, 0) / thisWeek.length
        : 0,
    };
  }, [database?.foodLog]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onAddFoodLog({
        id: existing?.id ?? `food-${todayStr}`,
        date: todayStr,
        fruitVegServings: fruitVeg,
        oilyFish,
        redMeat,
        highPurine,
        addedSugar,
        notes: notes || undefined,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>Daily food log</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Quick tick-list — aligns with your GP advice (5-a-day, oily fish 1–2×/wk, low-purine).
          </Typography>

          <Stack spacing={2.5} sx={{ mt: 2 }}>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Apple size={18} />
                <Typography variant="subtitle2" fontWeight={600}>Fruit & veg today</Typography>
                <Chip label={`${fruitVeg}/5`} size="small" color={fruitVeg >= 5 ? 'success' : 'default'} />
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <IconButton onClick={() => setFruitVeg((n) => Math.max(0, n - 1))}><Minus size={18} /></IconButton>
                <Box sx={{ flex: 1, display: 'flex', gap: 0.5 }}>
                  {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                    <Box
                      key={n}
                      onClick={() => setFruitVeg(n)}
                      sx={{
                        flex: 1,
                        height: 32,
                        borderRadius: 1,
                        backgroundColor: n <= fruitVeg ? 'primary.main' : 'action.hover',
                        cursor: 'pointer',
                        transition: 'background-color 150ms',
                      }}
                    />
                  ))}
                </Box>
                <IconButton onClick={() => setFruitVeg((n) => n + 1)}><Plus size={18} /></IconButton>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Week avg: {weekCounts.avgFruitVeg.toFixed(1)}/day
              </Typography>
            </Box>

            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Fish size={18} />
                <Typography variant="subtitle2" fontWeight={600}>Oily fish today?</Typography>
                <Chip label={`${weekCounts.oilyFish}/2 this wk`} size="small" />
              </Stack>
              <ToggleButtonGroup
                value={oilyFish ? 'yes' : 'no'}
                exclusive
                onChange={(_, v) => v !== null && setOilyFish(v === 'yes')}
                fullWidth
                size="small"
              >
                <ToggleButton value="no">No</ToggleButton>
                <ToggleButton value="yes">Yes</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Beef size={18} />
                <Typography variant="subtitle2" fontWeight={600}>Red meat today?</Typography>
                <Chip label={`${weekCounts.redMeat}× this wk`} size="small" />
              </Stack>
              <ToggleButtonGroup
                value={redMeat ? 'yes' : 'no'}
                exclusive
                onChange={(_, v) => v !== null && setRedMeat(v === 'yes')}
                fullWidth
                size="small"
              >
                <ToggleButton value="no">No</ToggleButton>
                <ToggleButton value="yes">Yes</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <AlertCircle size={18} />
                <Typography variant="subtitle2" fontWeight={600}>High-purine food?</Typography>
                <Chip label={`${weekCounts.highPurine}× this wk`} size="small" />
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Organ meats, anchovies, shellfish, beer — GP advised low-purine for uric acid.
              </Typography>
              <ToggleButtonGroup
                value={highPurine ? 'yes' : 'no'}
                exclusive
                onChange={(_, v) => v !== null && setHighPurine(v === 'yes')}
                fullWidth
                size="small"
              >
                <ToggleButton value="no">No</ToggleButton>
                <ToggleButton value="yes">Yes</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Added sugar today</Typography>
              <ToggleButtonGroup
                value={addedSugar}
                exclusive
                onChange={(_, v) => v && setAddedSugar(v)}
                fullWidth
                size="small"
              >
                <ToggleButton value="none">None</ToggleButton>
                <ToggleButton value="some">Some</ToggleButton>
                <ToggleButton value="lots">Lots</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <TextField
              label="Notes"
              size="small"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
              multiline
              maxRows={3}
            />

            <Button
              variant="contained"
              size="large"
              onClick={handleSave}
              disabled={saving}
            >
              {existing ? 'Update today' : 'Save today'}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};
